/**
 * Session Management Store
 * 
 * Handles persistent user session storage across browser sessions.
 * Stores session metadata locally while keeping actual auth tokens managed by Firebase.
 */

const SESSION_STORAGE_KEY = 'app:session';
const SESSION_METADATA_KEY = 'app:session:metadata';
const SESSION_EXPIRY_KEY = 'app:session:expiry';
const SESSION_ACTIVITY_KEY = 'app:session:lastActivity';

export interface SessionMetadata {
  uid: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  isAdmin?: boolean;
  isBeta?: boolean;
  authProvider?: 'phone' | 'password' | 'google' | 'unknown';
  lastActivity: number;
  createdAt: number;
  expiresAt: number;
}

/**
 * Save session metadata to localStorage
 * This persists user session info across browser restarts
 */
export function saveSessionMetadata(metadata: Omit<SessionMetadata, 'lastActivity' | 'createdAt' | 'expiresAt'>, sessionDurationMs = 30 * 24 * 60 * 60 * 1000): void {
  try {
    const now = Date.now();
    const existing = loadSessionMetadata();
    const preserveCreatedAt = existing?.uid === metadata.uid ? existing.createdAt : now;
    const sessionData: SessionMetadata = {
      ...metadata,
      lastActivity: now,
      createdAt: preserveCreatedAt,
      expiresAt: now + sessionDurationMs,
    };
    localStorage.setItem(SESSION_METADATA_KEY, JSON.stringify(sessionData));
    localStorage.setItem(SESSION_ACTIVITY_KEY, now.toString());
    console.debug('[sessionStore] Session metadata saved', { uid: metadata.uid });
  } catch (err) {
    console.warn('[sessionStore] Failed to save session metadata', err);
  }
}

/**
 * Load session metadata from localStorage
 */
export function loadSessionMetadata(): SessionMetadata | null {
  try {
    const data = localStorage.getItem(SESSION_METADATA_KEY);
    if (!data) return null;
    
    const session: SessionMetadata = JSON.parse(data);
    
    // Check if session has expired
    if (session.expiresAt && Date.now() > session.expiresAt) {
      console.debug('[sessionStore] Session expired, clearing');
      clearSession();
      return null;
    }
    
    return session;
  } catch (err) {
    console.warn('[sessionStore] Failed to load session metadata', err);
    return null;
  }
}

/**
 * Check if a valid session exists and hasn't expired
 */
export function hasValidSession(): boolean {
  const session = loadSessionMetadata();
  return session !== null && Date.now() <= (session.expiresAt || 0);
}

/**
 * Update last activity timestamp (used to track user engagement)
 */
export function updateSessionActivity(): void {
  try {
    const session = loadSessionMetadata();
    if (session) {
      session.lastActivity = Date.now();
      localStorage.setItem(SESSION_METADATA_KEY, JSON.stringify(session));
      localStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
    }
  } catch (err) {
    console.warn('[sessionStore] Failed to update session activity', err);
  }
}

/**
 * Get last activity timestamp
 */
export function getLastActivityTime(): number | null {
  try {
    const data = localStorage.getItem(SESSION_ACTIVITY_KEY);
    return data ? parseInt(data, 10) : null;
  } catch {
    return null;
  }
}

/**
 * Check if session is stale (no activity for specified duration)
 */
export function isSessionStale(maxInactivityMs = 7 * 24 * 60 * 60 * 1000): boolean {
  try {
    const lastActivity = getLastActivityTime();
    if (!lastActivity) return false;
    return Date.now() - lastActivity > maxInactivityMs;
  } catch {
    return false;
  }
}

/**
 * Extend session expiry time
 */
export function extendSession(additionalDurationMs = 7 * 24 * 60 * 60 * 1000): void {
  try {
    const session = loadSessionMetadata();
    if (session) {
      if (session.authProvider === 'phone') {
        // Phone-based sessions must remain short-lived even during active usage.
        session.expiresAt = Math.max(session.expiresAt, Date.now());
      } else {
        session.expiresAt = Math.max(session.expiresAt, Date.now()) + additionalDurationMs;
      }
      localStorage.setItem(SESSION_METADATA_KEY, JSON.stringify(session));
      console.debug('[sessionStore] Session extended');
    }
  } catch (err) {
    console.warn('[sessionStore] Failed to extend session', err);
  }
}

/**
 * Clear session data
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_METADATA_KEY);
    localStorage.removeItem(SESSION_ACTIVITY_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_EXPIRY_KEY);
    console.debug('[sessionStore] Session cleared');
  } catch (err) {
    console.warn('[sessionStore] Failed to clear session', err);
  }
}

/**
 * Get session duration remaining in milliseconds
 */
export function getSessionTimeRemaining(): number | null {
  try {
    const session = loadSessionMetadata();
    if (!session) return null;
    const remaining = session.expiresAt - Date.now();
    return remaining > 0 ? remaining : 0;
  } catch {
    return null;
  }
}

/**
 * Setup listener for storage changes across tabs
 */
export function subscribeToSessionChanges(callback: (session: SessionMetadata | null) => void): () => void {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === SESSION_METADATA_KEY) {
      try {
        const session: SessionMetadata | null = event.newValue ? JSON.parse(event.newValue) : null;
        callback(session);
      } catch (err) {
        console.warn('[sessionStore] Failed to parse session from storage event', err);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);
  
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
}

/**
 * Notify other tabs about session changes (cross-tab sync)
 */
export function notifySessionChange(session: SessionMetadata | null): void {
  try {
    if (session) {
      localStorage.setItem(SESSION_METADATA_KEY, JSON.stringify(session));
    } else {
      clearSession();
    }
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new CustomEvent('session-changed', { detail: { session } }));
  } catch (err) {
    console.warn('[sessionStore] Failed to notify session change', err);
  }
}

/**
 * Get a human-readable session status
 */
export function getSessionStatus(): {
  isValid: boolean;
  isStale: boolean;
  timeRemaining: number | null;
  lastActivity: number | null;
} {
  return {
    isValid: hasValidSession(),
    isStale: isSessionStale(),
    timeRemaining: getSessionTimeRemaining(),
    lastActivity: getLastActivityTime(),
  };
}
